import { z } from 'zod';

// ============================================================
// PER-SCAN FACE-RECOGNITION TELEMETRY (privacy-safe metrics ingest).
//
// The APK batches one row per 1:N face-match attempt and posts them to
// POST /api/field/telemetry (device-token, RAW response, fire-and-forget). The
// body carries METRICS ONLY — NO image, NO worker code, NO PII — so a face-scan
// health dashboard can chart scan-success %, latency, fallback rate and
// false-reject signals per device / worksite / day. Idempotent by `uuid`.
// ============================================================
export const FieldScanOutcomeSchema = z.enum(['MATCHED', 'FAILED', 'FALLBACK']);
export type FieldScanOutcome = z.infer<typeof FieldScanOutcomeSchema>;

export const FieldScanMatchScopeSchema = z.enum(['EXPECTED', 'FULL']);
export type FieldScanMatchScope = z.infer<typeof FieldScanMatchScopeSchema>;

export const FieldScanTelemetrySchema = z.object({
  // Per-scan idempotency key (APK-generated uuid; a replay is skipped on ingest).
  uuid: z.string().min(8).max(64),
  outcome: FieldScanOutcomeSchema,
  capturedAt: z.number().int().positive(), // APK wall-clock of the scan, unix millis
  bestScore: z.number().min(0).max(1).optional(), // top1 cosine
  top2Margin: z.number().min(-1).max(1).optional(), // top1 - top2
  liveness: z.number().min(0).max(1).optional(),
  latencyMs: z.number().int().min(0).max(10000).optional(),
  gallerySizeSearched: z.number().int().min(0).optional(),
  matchScope: FieldScanMatchScopeSchema.optional(),
});
export type FieldScanTelemetry = z.infer<typeof FieldScanTelemetrySchema>;

export const FieldScanTelemetryRequestSchema = z.object({
  scans: z.array(FieldScanTelemetrySchema).min(1).max(500),
});
export type FieldScanTelemetryRequest = z.infer<typeof FieldScanTelemetryRequestSchema>;

// The ingest route ALWAYS responds 202 with this shape (even on a storage error)
// so telemetry never blocks or fails a punch. It never echoes ingested data.
export const FieldScanTelemetryAckSchema = z.object({ ok: z.literal(true) });
export type FieldScanTelemetryAck = z.infer<typeof FieldScanTelemetryAckSchema>;

// ── Admin aggregation (GET /api/field/telemetry/summary) ─────────────────────
// One rollup row per device / worksite / day over the queried window. Surfaced
// in the web face-scan health dashboard; `belowThreshold` flags a failing gate.
export const FieldScanTelemetrySummaryQuerySchema = z.object({
  deviceId: z.string().min(1).optional(),
  worksiteId: z.string().min(1).optional(),
  // ISO date (YYYY-MM-DD) or full ISO datetime; default window is the last 24h.
  startDate: z.string().min(1).optional(),
  endDate: z.string().min(1).optional(),
});
export type FieldScanTelemetrySummaryQuery = z.infer<
  typeof FieldScanTelemetrySummaryQuerySchema
>;

export const FieldScanTelemetrySummaryRowSchema = z.object({
  deviceId: z.string(),
  worksiteId: z.string(),
  day: z.string(), // calendar day key (YYYY-MM-DD) of capturedAt
  scanCount: z.number().int().nonnegative(),
  successRate: z.number().min(0).max(1), // MATCHED / (MATCHED + FAILED)
  fallbackRate: z.number().min(0).max(1), // FALLBACK / total
  p50LatencyMs: z.number().nonnegative().nullable(),
  p95LatencyMs: z.number().nonnegative().nullable(),
  avgGallerySize: z.number().nonnegative().nullable(),
  lowMarginCount: z.number().int().nonnegative(), // top2Margin < policy.margin
  belowThreshold: z.boolean(), // successRate < 0.95
});
export type FieldScanTelemetrySummaryRow = z.infer<
  typeof FieldScanTelemetrySummaryRowSchema
>;

export const FieldScanTelemetrySummaryResponseSchema = z.object({
  rows: z.array(FieldScanTelemetrySummaryRowSchema),
});
export type FieldScanTelemetrySummaryResponse = z.infer<
  typeof FieldScanTelemetrySummaryResponseSchema
>;

/** The installer verdict this endpoint will accept, capped like a crash note. */
export const FIELD_INSTALL_FAILURE_MAX_CHARS = 200;

/**
 * `GET /api/field/app-update` — what a device says about itself when it asks
 * whether there is a newer APK.
 *
 * BOTH FIELDS ARE OPTIONAL, and that is the deployable half of the contract.
 * Every APK in the field today sends neither, because neither existed when it
 * was built; a schema that required them would answer 422 to the whole fleet
 * the moment somebody pointed a device at this endpoint. Absent means UNKNOWN,
 * and the server resolves unknown toward offering the release — a redundant
 * download costs bandwidth, a tablet that can never learn about an update costs
 * a site visit.
 *
 * `versionCode` is the one the answer turns on: it is Android's own monotonic
 * install-ordering integer, and it is what the rollback guard compares.
 * `version` is the human-readable name, recorded so the console's adoption
 * table can print something a foreman recognises, and read once more to decide
 * `mandatory` against the existing `APP_MIN_VERSION` floor.
 */
export const FieldAppUpdateQuerySchema = z.object({
  version: z.string().trim().min(1).max(32).optional(),
  versionCode: z.coerce.number().int().min(0).max(2_100_000_000).optional(),
  /**
   * WHY THE LAST INSTALL DID NOT HAPPEN, in the platform's own words.
   *
   * The device already knows: `UpdateInstallStatus` keeps the installer's
   * verdict — `INSTALL_FAILED_VERSION_DOWNGRADE`,
   * `INSTALL_FAILED_UPDATE_INCOMPATIBLE` (a build signed with another key),
   * `INSTALL_FAILED_INSUFFICIENT_STORAGE` — and the tablet's own settings
   * screen shows it. NOBODY ELSE EVER SAW IT. An operator in the office
   * watching a publish that "went out" has no way to tell a fleet that is
   * downloading slowly from a fleet that is failing to install, one tablet at
   * a time, for a reason the tablet could have told them six hours ago.
   *
   * On the update CHECK rather than an endpoint of its own, for the same
   * reason the reported build is: an endpoint the device has no other reason
   * to call is an endpoint that reports nothing. It travels with the request
   * the device already makes.
   *
   * The device sends it ONCE per failure and clears it; the server also
   * refuses to record the same failure twice for the same device, so a client
   * that keeps sending it cannot fill the audit log.
   */
  installFailure: z.string().trim().min(1).max(FIELD_INSTALL_FAILURE_MAX_CHARS).optional(),
  /** The build the failed install was OF, which is rarely the running one. */
  installFailureCode: z.coerce.number().int().min(0).max(2_100_000_000).optional(),
});
export type FieldAppUpdateQuery = z.infer<typeof FieldAppUpdateQuerySchema>;
